// The Options tab of a store product: the options a buyer picks (each from
// an option set, StoreOptionSet in the CMS, or with values of its own) and
// the Variants grid the variant-axis options span — one SKU per combination,
// with its own EAN, stock, price, images, on / off.
//
// The tab edits the product's `options` and `variants` fields in the form
// (`onChange`), so the dialog's Save writes them with the rest; its
// registered save checks them first (keys, SKUs) and hands the form the
// variant rows the axes span, new ones included.
import React, { useEffect, useMemo, useRef, useState } from "react"
import { ArrowPathIcon, ArrowUturnLeftIcon, ChevronDownIcon, ChevronUpIcon, PhotoIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import Checkbox from "aldoo-ra/Checkbox"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import { GetCMSContent } from "aldoo-ra/CMS/api"
import { OptionValuesList, ImageField } from "aldoo-ra/CMS/field-editors/store-option-values"
import { LanguageTag, useEditingLanguage } from "aldoo-ra/Admin/Store/Products/EditingLanguage"
import { api } from "aldoo-ra/API"
import { priceDisplayFormat } from "aldoo-ra/CMSModels/StorePrice/display-formatters"
import { labelText, candidateValues, offeredValues, variantRows, asVariant, generateSku, problemsOf, withKeys } from "./model"

const DISPLAYS = ["text", "color", "image", "color+image"]
const SET_TYPE = "StoreOptionSet"

let cachedSets = null

/** The option sets (CMS StoreOptionSet), loaded once per page. */
function useOptionSets() {
  const [sets, setSets] = useState(cachedSets)
  const [error, setError] = useState(null)
  useEffect(() => {
    if (cachedSets) return
    let alive = true
    GetCMSContent({ match: { "content.type": SET_TYPE, deleted: { $ne: true } }, itemsPerPage: 1000, expand: false })
      .then((res) => {
        if (res?.error) throw new Error(res.error)
        const list = (res?.data || [])
          .map((node) => ({ _id: node._id, key: String(node.content?.key || "").trim(), label: node.content?.label, display: node.content?.display?.value || node.content?.display || "text", values: node.content?.values || [] }))
          .filter((set) => set.key)
          .sort((a, b) => a.key.localeCompare(b.key))
        cachedSets = list
        if (alive) setSets(list)
      })
      .catch((e) => alive && setError(e?.message || "Failed to load the option sets"))
    return () => {
      alive = false
    }
  }, [])
  return { sets: sets || [], loading: !sets && !error, error }
}

/** A single-choice DropdownSelect over { value, name } options, valued by the option's value. */
function Select({ options, value, onChange, placeholder, className = "" }) {
  const selected = options.find((option) => option.value === (value ?? ""))
  return (
    <DropdownSelect
      variant="admin"
      className={className}
      options={options}
      value={selected ? [selected] : []}
      placeholder={placeholder}
      allowEmptySelection={false}
      enableResetButton={false}
      showItemNamesInSelection
      showCheckbox={false}
      showSelectionTick
      onChange={(picked) => {
        const option = Array.isArray(picked) ? picked[0] : picked
        if (option) onChange?.(option.value)
      }}
    />
  )
}

const Label = ({ children }) => <span className="block text-xs font-medium text-admin_text_secondary mb-1">{children}</span>

/** A number typed in an Input: empty is null. */
const numberOf = (text) => {
  const value = String(text ?? "").trim().replace(",", ".")
  if (!value) return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

/**
 * The product's own price, which a variant without a price of its own sells at: the primary
 * real model of its StorePrice ({ amount, currency }, major units, the unit a variant's price
 * is in), or null. `price` is the product's field: the record, or its id (then loaded).
 */
function useProductPrice(price) {
  const [loaded, setLoaded] = useState(null)
  const id = price && typeof price === "object" ? price._id : price
  const record = price && typeof price === "object" && Array.isArray(price.price_model) ? price : loaded
  useEffect(() => {
    if (!id || (price && typeof price === "object" && Array.isArray(price.price_model))) return
    let alive = true
    api("crud")({ get: { model: "StorePrice", match: { _id: id }, limit: 1 } })
      .then((res) => alive && setLoaded(Array.isArray(res) ? res[0] || null : null))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [id])
  const models = Array.isArray(record?.price_model) ? record.price_model.filter((model) => model?.type === "real") : []
  const model = models.find((entry) => entry.primary) || models[0]
  return model && Number(model.amount) >= 0 ? { amount: Number(model.amount), currency: model.currency } : null
}

/**
 * A variant's price: its own, or the product's when it has none. An imported row's is the
 * source's and only shown; a row's own is never below 0, and goes back to the product's with
 * the arrow beside it.
 */
function VariantPrice({ row, locked, productPrice, onChange }) {
  const productText = productPrice ? priceDisplayFormat(productPrice.amount, productPrice.currency) : ""
  const ownText = row.price !== null && row.price !== undefined && productPrice ? priceDisplayFormat(row.price, productPrice.currency) : row.price ?? ""
  if (locked) {
    return row.price !== null && row.price !== undefined ? (
      <span className="tabular-nums" data-testid="variant-price">{ownText}</span>
    ) : (
      <span className="text-admin_text_secondary tabular-nums" title="The product's price" data-testid="variant-price">
        {productText || "product's"}
      </span>
    )
  }
  const own = row.price !== null && row.price !== undefined
  return (
    <div className="flex items-center gap-1" data-testid="variant-price-own">
      <Input
        variant="admin"
        type="number"
        min={0}
        value={row.price ?? ""}
        placeholder={productText || "product's"}
        onChange={(e) => {
          const amount = numberOf(e.target.value)
          // a price below 0 is no price: the field keeps what it had
          if (amount !== null && amount < 0) return
          onChange(amount)
        }}
      />
      {own && (
        <Button variant="admin_icon" title={productText ? `Back to the product's price (${productText})` : "Back to the product's price"} onClick={() => onChange(null)} data-testid="variant-price-reset">
          <ArrowUturnLeftIcon className="w-4 h-4" />
        </Button>
      )}
    </div>
  )
}

/** One option: where its values come from, which are offered, how they show, what they add. */
// `lang`: the language the labels are edited and shown in (the dialog's); `base`: the language a
// label written as plain text is in
// `renderRows({ toggle })`: the product's only variant axis shows its variants here, one row
// per value of the set (ticked: offered, with its SKU and stock), instead of the value
// checkboxes and a table of its own below
// `usedValues`: this option's values the product's stored variants hold — a set without one
// of them would leave those variants with nothing to name them, so it is not offered;
// `importer`: the import the product comes from, which chose the set (read-only then)
function OptionCard({ option, index, count, sets, onChange, onRemove, onMove, lang, base, renderRows = null, usedValues = [], importer = null }) {
  const set = option.set ? sets.find((entry) => entry.key === option.set) : null
  const candidates = candidateValues(option, sets)
  const offered = offeredValues(option, sets)
  const allowed = Array.isArray(option.allowed) ? option.allowed.filter(Boolean) : []
  const patch = (fields) => onChange({ ...option, ...fields })
  // the label in each language: a plain text is the base language's
  const labels = typeof option.label === "string" ? (option.label ? { [base]: option.label } : {}) : { ...(option.label && typeof option.label === "object" ? option.label : {}) }
  const setLabel = (text) => {
    const next = { ...labels }
    if (text) next[lang] = text
    else delete next[lang]
    patch({ label: next })
  }

  const toggleAllowed = (key) => {
    const current = allowed.length ? allowed : candidates.map((value) => value.key)
    const next = current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key]
    // every value ticked is "all": the set's new values come along
    patch({ allowed: next.length === candidates.length ? [] : candidates.map((value) => value.key).filter((entry) => next.includes(entry)) })
  }

  const setDelta = (key, text) => {
    const deltas = { ...(option.priceDelta || {}) }
    const amount = numberOf(text)
    if (amount) deltas[key] = amount
    else delete deltas[key]
    patch({ priceDelta: deltas })
  }

  // only the sources that name every value the variants already use (the current one always)
  const fits = (keys) => usedValues.every((key) => keys.includes(key))
  const ownKeys = (option.values || []).map((value) => String(value?.key || "").trim())
  const sourceOptions = [
    ...(!option.set || fits(ownKeys) ? [{ value: "", name: "Own values" }] : []),
    ...sets
      .filter((entry) => entry.key === option.set || fits((entry.values || []).map((value) => String(value?.key || "").trim())))
      .map((entry) => ({ value: entry.key, name: `${labelText(entry.label) || entry.key} (${entry.key})` })),
  ]
  if (option.set && !set) sourceOptions.push({ value: option.set, name: `${option.set} (not found)` })
  const setLocked = !!importer && !!option.set
  // a way to show the values only when they have what it shows: a color, an image, both;
  // the set's own way is its first entry, not repeated; the one picked stays listed
  const hasColor = candidates.some((value) => value?.hex)
  const hasImage = candidates.some((value) => value?.image)
  const possible = DISPLAYS.filter((display) => display === "text" || (display === "color" && hasColor) || (display === "image" && hasImage) || (display === "color+image" && hasColor && hasImage))
  // the set's way (or text): a product's own "text" on a text set is the same thing
  const defaultDisplay = set ? set.display || "text" : "text"
  const displayOptions = [
    { value: "", name: set ? `As the set says (${set.display})` : "Text" },
    ...DISPLAYS.filter((display) => display !== defaultDisplay && (possible.includes(display) || display === option.display)).map((display) => ({ value: display, name: display })),
  ]
  // nothing to choose between: no picker
  const showDisplay = displayOptions.length > 1

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-admin_bordered p-4" data-testid={`option-${option.key || index}`}>
      <div className="flex flex-wrap items-end gap-3">
        {/* the key is worked out from the set or the label (model.js deriveKey): not asked;
            a label is a word or two, so its field is as wide as one */}
        <div className="w-full sm:w-64">
          <Label>
            <span className="inline-flex items-center gap-1.5">
              Label <LanguageTag language={lang} />
            </span>
          </Label>
          <Input variant="admin" value={labels[lang] || ""} placeholder={labelText(option.label, base) || labelText(set?.label) || "Size"} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="w-56">
          <Label>Values from</Label>
          {setLocked ? (
            // the import chose the set: its sizes are the source's
            <div className="h-10 flex flex-col justify-center text-sm" title={`Chosen by the import (${importer})`} data-testid="option-set-locked">
              <span className="text-admin_text truncate">{labelText(set?.label) || option.set}</span>
              <span className="text-[11px] text-admin_text_secondary">from the import</span>
            </div>
          ) : (
            <>
              <Select options={sourceOptions} value={option.set || ""} onChange={(key) => patch({ set: key || undefined, allowed: [] })} />
              {usedValues.length > 0 && sourceOptions.length < sets.length + 1 && (
                <span className="block mt-1 text-[11px] text-admin_text_secondary">Only the sets with every value the variants use</span>
              )}
            </>
          )}
        </div>
        {showDisplay && (
          <div className="w-48">
            <Label>Display</Label>
            <Select options={displayOptions} value={option.display && option.display !== defaultDisplay ? option.display : ""} onChange={(display) => patch({ display: display || undefined })} />
          </div>
        )}
        <div className="flex items-center ml-auto">
          <Button variant="admin_icon" title="Up" disabled={index === 0} onClick={() => onMove(index - 1)}>
            <ChevronUpIcon className="w-4 h-4" />
          </Button>
          <Button variant="admin_icon" title="Down" disabled={index === count - 1} onClick={() => onMove(index + 1)}>
            <ChevronDownIcon className="w-4 h-4" />
          </Button>
          <Button variant="admin_icon" title="Remove the option" onClick={onRemove}>
            <TrashIcon className="w-4 h-4 text-admin_error" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-6">
        {/* a variant axis is always picked (no SKU without it): the switch is only a customization's */}
        {!option.variantAxis && (
          <div className="flex items-center gap-2">
            <ToggleSwitch variant="admin" isOn={option.required !== false} onChange={(on) => patch({ required: !!on })} />
            <span className="text-sm">Required</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <ToggleSwitch variant="admin" isOn={!!option.variantAxis} onChange={(on) => patch({ variantAxis: !!on, ...(on ? { priceDelta: undefined, required: true } : {}) })} />
          <span className="text-sm">Variant axis</span>
          <span className="text-xs text-admin_text_secondary">{option.variantAxis ? "each value is a SKU with its own stock" : "a customization: a price delta, no stock"}</span>
        </div>
      </div>

      {option.set && renderRows ? (
        renderRows({ toggle: toggleAllowed })
      ) : option.set ? (
        <div className="flex flex-col gap-2">
          <Label>Offered values {allowed.length ? `(${offered.length} of ${candidates.length})` : "(all)"}</Label>
          {!candidates.length && <Typography variant="admin_body2" className="opacity-70">{set ? "The set has no values." : "The set is not found."}</Typography>}
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {candidates.map((value) => (
              <Checkbox key={value.key} variant="admin" label={labelText(value.label, lang) && labelText(value.label, lang) !== value.key ? `${value.key} · ${labelText(value.label, lang)}` : value.key} isOn={!allowed.length || allowed.includes(value.key)} onChange={() => toggleAllowed(value.key)} />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Label>Values</Label>
          <OptionValuesList value={option.values || []} onChange={(values) => patch({ values })} language={lang} base={base} />
          {renderRows && renderRows({ toggle: toggleAllowed })}
        </div>
      )}

      {!option.variantAxis && offered.length > 0 && (
        <div className="flex flex-col gap-2">
          <Label>Price deltas (added to the product's price, in its currency)</Label>
          <div className="flex flex-wrap gap-3">
            {offered.map((value) => (
              <div key={value.key} className="w-32">
                <Input
                  variant="admin"
                  type="number"
                  label={labelText(value.label, lang) || value.key}
                  value={option.priceDelta?.[value.key] ?? ""}
                  placeholder="0"
                  onChange={(e) => setDelta(value.key, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/** The variants the axis options span: one row per combination. */
// `importer`: the importer the product comes from (product.source.importer), else null. Its rows
// are the source's items: their SKU, EAN and price are the import's — the next import matches the
// rows by SKU and writes them again — so they show read-only; stock, the switch and the images
// stay the admin's (the import keeps them; a source out of stock sets the stock to 0).
// `single`: the product's only axis — { option, toggle(valueKey) }: every value of it is a row,
// a tick at its start says whether it is offered; an unticked one is a dimmed line
function VariantsGrid({ options, variants, sets, parentSku, onChange, lang, importer = null, productPrice = null, single = null, loadedVariants = null }) {
  const { rows, orphans, axes } = variantRows(options, variants, sets, parentSku)
  // the rows the product came with when the dialog opened: what the import wrote (a row made
  // here since — a size ticked, another set picked — is not the import's)
  const importedSkus = useMemo(() => new Set(importer ? (loadedVariants || []).map((variant) => String(variant?.sku || "").trim()).filter(Boolean) : []), [importer, loadedVariants])
  const isImported = (row) => !!importer && !row.isNew && importedSkus.has(String(row.sku || "").trim())
  const anyImported = rows.some(isImported)
  const [openImages, setOpenImages] = useState({})
  const [bulkStock, setBulkStock] = useState("")

  if (!axes.length) {
    return (
      <Typography variant="admin_body2" className="opacity-70">
        No option is a variant axis: the product sells as one SKU, with its own stock.
      </Typography>
    )
  }

  const labelsOf = (combination) =>
    axes
      .map((axis) => {
        const value = offeredValues(axis, sets).find((entry) => entry.key === combination[axis.key])
        return labelText(value?.label, lang) || combination[axis.key]
      })
      .join(" / ")

  // every row is written (a new one becomes a variant once anything is set on any row)
  const commit = (nextRows, nextOrphans = orphans) => onChange([...nextRows.map(asVariant), ...nextOrphans])
  const update = (index, patch) => commit(rows.map((row, at) => (at === index ? { ...row, ...patch } : row)))

  const generate = () => {
    const empty = rows.some((row) => !String(row.sku || "").trim())
    // fills the empty ones; with none empty, all are made again
    // an imported row keeps the source's SKU
    commit(rows.map((row) => (isImported(row) || (empty && String(row.sku || "").trim()) ? row : { ...row, sku: generateSku(parentSku, row.options, axes) })))
  }

  const candidatesOf = (option) => candidateValues(option, sets)
  const valueText = (value) => {
    const text = labelText(value.label, lang)
    return text && text !== value.key ? `${value.key} · ${text}` : value.key
  }
  // one variant's row; `tick`: the offered box at its start (the single axis)
  const rowView = (row, index, tick) => {
    const locked = isImported(row)
    return (
              <React.Fragment key={JSON.stringify(row.options)}>
                <tr className={`border-b border-admin_bordered ${row.enabled === false ? "opacity-60" : ""}`} data-testid={locked ? "variant-imported" : "variant-own"}>
                  {tick}
                  <td className="p-2 whitespace-nowrap font-medium">
                    {labelsOf(row.options)}
                    {row.isNew && <span className="ml-2 text-xs text-admin_primary font-normal">new</span>}
                    {locked && <span className="ml-2 text-xs text-admin_text_secondary font-normal" title={`From the import (${importer})`}>imported</span>}
                    {/* a row the import does not send goes with the next import */}
                    {importer && !locked && <span className="block text-[11px] text-admin_warning font-normal" title="The next import writes the variants again from the source">not in the import</span>}
                  </td>
                  {/* an imported row's codes are the source's: text, not fields */}
                  <td className="p-2 w-[200px]">
                    {locked ? (
                      <span className="font-mono text-xs whitespace-nowrap" data-testid="variant-sku">{row.sku}</span>
                    ) : (
                      <Input variant="admin" className="font-mono text-xs" value={row.sku || ""} placeholder={generateSku(parentSku, row.options, axes)} onChange={(e) => update(index, { sku: e.target.value.trim() })} />
                    )}
                  </td>
                  <td className="p-2 w-[160px]">
                    {locked ? (
                      <span className="font-mono text-xs whitespace-nowrap text-admin_text_secondary" data-testid="variant-ean">{row.ean || "—"}</span>
                    ) : (
                      <Input variant="admin" className="font-mono text-xs" value={row.ean || ""} onChange={(e) => update(index, { ean: e.target.value.trim() })} />
                    )}
                  </td>
                  <td className="p-2">
                    <Input
                      variant="admin"
                      type="number"
                      value={row.stock ?? ""}
                      placeholder="not tracked"
                      onChange={(e) => {
                        const stock = numberOf(e.target.value)
                        update(index, { stock: stock === null ? null : Math.max(0, Math.round(stock)) })
                      }}
                    />
                  </td>
                  <td className="p-2">
                    <VariantPrice row={row} locked={locked} productPrice={productPrice} onChange={(price) => update(index, { price })} />
                  </td>
                  <td className="p-2">
                    <Button variant="admin_text" onClick={() => setOpenImages((state) => ({ ...state, [index]: !state[index] }))}>
                      <PhotoIcon className="w-4 h-4" /> {row.images?.length || 0}
                    </Button>
                  </td>
                  <td className="p-2">
                    <ToggleSwitch variant="admin" isOn={row.enabled !== false} onChange={(on) => update(index, { enabled: !!on })} />
                  </td>
                </tr>
                {openImages[index] && (
                  <tr className="border-b border-admin_bordered">
                    <td colSpan={single ? 8 : 7} className="p-2">
                      <ImageField multiple value={row.images || []} onChange={(images) => update(index, { images })} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {anyImported && (
        <Typography variant="admin_body2" className="opacity-70" data-testid="variants-imported-note">
          Imported ({importer}): the SKU, the EAN and the price of an imported row are the source's and follow each import. The stock, the images and the switch are yours; the source going out of stock sets the stock to 0.
        </Typography>
      )}
      <div className="flex flex-wrap items-end gap-3">
        {/* only what can act now: no SKUs to make where every row is imported, the bulk stock
            once a number is typed, enable / disable where a row is the other way */}
        {!(rows.length > 0 && rows.every(isImported)) && (
          <Button variant="admin_secondary" onClick={generate} title={`{${parentSku}}-{value}: fills the empty SKUs; with none empty, makes them all again`}>
            <ArrowPathIcon className="w-4 h-4" /> Generate SKUs
          </Button>
        )}
        <div className="flex items-end gap-2">
          <div className="w-28">
            <Input variant="admin" type="number" min={0} value={bulkStock} placeholder="Stock" onChange={(e) => setBulkStock(e.target.value)} />
          </div>
          {numberOf(bulkStock) !== null && numberOf(bulkStock) >= 0 && (
            <Button variant="admin_secondary" onClick={() => commit(rows.map((row) => ({ ...row, stock: Math.max(0, Math.round(numberOf(bulkStock))) })))}>
              Set stock for all
            </Button>
          )}
        </div>
        {rows.some((row) => row.enabled === false) && (
          <Button variant="admin_secondary" onClick={() => commit(rows.map((row) => ({ ...row, enabled: true })))}>
            Enable all
          </Button>
        )}
        {rows.some((row) => row.enabled !== false) && (
          <Button variant="admin_secondary" onClick={() => commit(rows.map((row) => ({ ...row, enabled: false })))}>
            Disable all
          </Button>
        )}
      </div>

      <div className="overflow-x-auto border border-admin_bordered rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-admin_text_secondary border-b border-admin_bordered">
              {single && <th className="p-2 w-10" title="Offered: the buyer can pick it" />}
              <th className="p-2 font-medium">{single ? labelText(single.option.label, lang) || "Value" : "Variant"}</th>
              <th className="p-2 font-medium">SKU</th>
              <th className="p-2 font-medium">EAN</th>
              <th className="p-2 font-medium w-24">Stock</th>
              <th className="p-2 font-medium w-44" title="A variant without a price of its own sells at the product's">Price</th>
              <th className="p-2 font-medium">Images</th>
              <th className="p-2 font-medium">Enabled</th>
            </tr>
          </thead>
          <tbody>
            {single
              ? candidatesOf(single.option).map((value) => {
                  const index = rows.findIndex((row) => row.options?.[single.option.key] === value.key)
                  const offered = index >= 0
                  const tick = (
                    <td className="p-2 w-10">
                      <Checkbox variant="admin" isOn={offered} onChange={() => single.toggle(value.key)} />
                    </td>
                  )
                  if (offered) return rowView(rows[index], index, tick)
                  // not offered: the value, dimmed, nothing to fill in
                  return (
                    <tr key={`off-${value.key}`} className="border-b border-admin_bordered opacity-50" data-testid="variant-off">
                      {tick}
                      <td className="p-2 whitespace-nowrap font-medium">{valueText(value)}</td>
                      <td colSpan={6} className="p-2 text-xs text-admin_text_secondary">Not offered</td>
                    </tr>
                  )
                })
              : rows.map((row, index) => rowView(row, index, null))}
          </tbody>
        </table>
      </div>

      {orphans.length > 0 && (
        <div className="flex flex-col gap-2 rounded-lg border border-admin_warning p-3">
          <Typography variant="admin_body2">Variants no combination of the options names any more (kept until removed; not sold):</Typography>
          {orphans.map((variant, index) => (
            <div key={`${variant.sku}-${index}`} className="flex items-center gap-3 text-sm">
              <span className="font-mono">{variant.sku || "—"}</span>
              <span className="text-admin_text_secondary">{Object.entries(variant.options || {}).map(([key, value]) => `${key}: ${value}`).join(", ")}</span>
              <Button variant="admin_icon" title="Remove" onClick={() => commit(rows, orphans.filter((_, at) => at !== index))}>
                <TrashIcon className="w-4 h-4 text-admin_error" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** An option as it is stored: no empty leftovers, deltas only on a customization. */
const cleanOption = (option) => {
  const out = { ...option, key: String(option.key || "").trim() }
  if (!labelText(out.label)) delete out.label
  if (!out.set) delete out.set
  if (!out.display) delete out.display
  if (out.set && !(out.values || []).length) delete out.values
  if (!(out.allowed || []).length) delete out.allowed
  const deltas = Object.entries(out.priceDelta || {}).filter(([, amount]) => Number.isFinite(Number(amount)) && Number(amount) !== 0)
  if (out.variantAxis || !deltas.length) delete out.priceDelta
  else out.priceDelta = Object.fromEntries(deltas.map(([key, amount]) => [key, Number(amount)]))
  out.required = out.variantAxis ? true : out.required !== false
  out.variantAxis = !!out.variantAxis
  delete out._autoKey
  // a variant axis is always picked: there is no SKU without it
  if (out.variantAxis) out.required = true
  return out
}

export default function OptionsSection({ value, product, onChange, register, language }) {
  const productPrice = useProductPrice(value?.price ?? product?.price)
  // the dialog's language; a label written as plain text is in the product's first language
  const editing = useEditingLanguage()
  const lang = language || editing?.language || "en"
  const base = (Array.isArray(product?.languages) && product.languages[0]) || editing?.primary || "en"
  const { sets, loading, error } = useOptionSets()
  const options = useMemo(() => (Array.isArray(value?.options) ? value.options : []), [value?.options])
  const variants = useMemo(() => (Array.isArray(value?.variants) ? value.variants : []), [value?.variants])
  // a product-level SKU (the identifiers extension) names the variants, else the pid
  const parentSku = value?.sku || value?.pid || product?.pid || ""

  const latest = useRef(null)
  latest.current = { options, variants, sets, loaded: !loading && !error, parentSku }

  useEffect(() => {
    register?.({
      save: async () => {
        const { options: given, variants: stored, sets: known, loaded, parentSku: parent } = latest.current
        // every option with its key (worked out where it has none)
        const current = withKeys(given, known)
        if (!current.length && !stored.length) return {}
        // without the sets the values are not known: the fields are saved as they are
        if (!loaded) return {}
        const { rows, orphans } = variantRows(current, stored, known, parent)
        const problems = problemsOf(current, rows, known)
        if (problems.length) {
          showNotification({ message: `Options: ${problems.join(". ")}`, className: "bg-admin_error text-white" })
          return false
        }
        return { options: current.map(cleanOption), variants: [...rows.map(asVariant), ...orphans] }
      },
    })
    return () => register?.(null)
  }, [])

  // the variants follow the options: a new combination is a variant at once
  // (its SKU generated), so the form holds them even if this tab is left
  // the variants this tab wrote last: any other change of them came from outside (a change
  // taken back in the save summary), and the ticks follow it — below
  const sentVariants = useRef(null)
  const emit = (patch) => {
    if (patch.variants) sentVariants.current = patch.variants
    onChange?.(patch)
  }
  const setOptions = (given, current = variants) => {
    // an option without a key gets the one its set or label says
    const next = withKeys(given, sets)
    if (loading || error) return emit({ options: next, ...(current !== variants ? { variants: current } : {}) })
    const { rows, orphans } = variantRows(next, current, sets, parentSku)
    // the stored variants keep their places (an unticked size does not move to the end): only
    // what really changed reads as a change
    const place = new Map(current.map((variant, index) => [variant?.sku || JSON.stringify(variant?.options), index]))
    const placeOf = (variant) => place.get(variant?.sku || JSON.stringify(variant?.options)) ?? Number.MAX_SAFE_INTEGER
    const ordered = [...rows.map(asVariant), ...orphans].map((variant, index) => ({ variant, index })).sort((a, b) => placeOf(a.variant) - placeOf(b.variant) || a.index - b.index).map((entry) => entry.variant)
    emit({ options: next, variants: ordered })
  }
  const updateOption = (index, given) => {
    // a new option's key follows its set and label (withKeys): worked out before the variants follow it
    const option = withKeys(options.map((entry, at) => (at === index ? given : entry)), sets)[index]
    const before = options[index]?.key
    const next = options.map((entry, at) => (at === index ? option : entry))
    if (!before || !option.key || before === option.key) return setOptions(next)
    // a renamed option keeps its variants: their choices follow the new key
    const renamed = variants.map((variant) => {
      if (!variant?.options || !(before in variant.options)) return variant
      const { [before]: chosen, ...rest } = variant.options
      return { ...variant, options: option.key ? { ...rest, [option.key]: chosen } : rest }
    })
    setOptions(next, renamed)
  }
  const moveOption = (from, to) => {
    if (to < 0 || to >= options.length) return
    const next = [...options]
    const [option] = next.splice(from, 1)
    next.splice(to, 0, option)
    setOptions(next)
  }
  const axes = options.filter((option) => option?.variantAxis && option.key)
  // the variants as the dialog opened them (the product as loaded): which rows are the import's
  const loadedVariants = Array.isArray(product?.variants) ? product.variants : []
  const gridProps = { loadedVariants, options, variants, sets, parentSku, onChange: (next) => emit({ variants: next }), lang, importer: value?.source?.importer || product?.source?.importer || null, productPrice }
  // A size's row taken back from outside: an added size goes unticked, a removed one ticked
  // again — else the grid would make the row anew and the next save write it back.
  const previousVariants = useRef(variants)
  useEffect(() => {
    const previous = previousVariants.current
    previousVariants.current = variants
    if (variants === sentVariants.current || previous === variants || axes.length !== 1 || loading || error) return
    const axis = axes[0]
    const valuesOf = (list) => new Set((list || []).map((variant) => variant?.options?.[axis.key]).filter(Boolean))
    const before = valuesOf(previous)
    const now = valuesOf(variants)
    const dropped = [...before].filter((key) => !now.has(key))
    const back = [...now].filter((key) => !before.has(key))
    if (!dropped.length && !back.length) return
    const candidates = candidateValues(axis, sets).map((value) => value.key)
    const current = Array.isArray(axis.allowed) && axis.allowed.length ? axis.allowed : candidates
    const next = candidates.filter((key) => (current.includes(key) && !dropped.includes(key)) || back.includes(key))
    if (!next.length || (next.length === current.length && next.every((key) => current.includes(key)))) return
    const allowed = next.length === candidates.length ? [] : next
    onChange?.({ options: options.map((option) => (option === axis ? { ...option, allowed } : option)) })
  }, [variants])

  // a new option has no key yet: it takes one from the set or the label it is given
  const addOption = () => setOptions([...options, { key: "", required: true, variantAxis: !options.some((option) => option.variantAxis), allowed: [] }])

  return (
    <div className="flex flex-col gap-6" data-testid="options-section">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <Typography component="h4" variant="admin_h5" className="my-0">
            Options
          </Typography>
          <Button variant="admin_secondary" onClick={addOption}>
            <PlusIcon className="w-4 h-4" /> Add option
          </Button>
        </div>
        <Typography variant="admin_body2" className="opacity-70">
          What the buyer picks. The values come from an option set (CMS → Option Set) or are the option's own; a variant axis makes a SKU per value.
        </Typography>
        {error && <Typography variant="admin_body2" className="text-admin_error">{error}</Typography>}
        {loading && <Typography variant="admin_body2" className="opacity-70">Loading the option sets…</Typography>}
        {!options.length && (
          <Typography variant="admin_body2" className="opacity-70">
            No options: the product has nothing to choose.
          </Typography>
        )}
        {options.map((option, index) => (
          <OptionCard
            key={index}
            option={option}
            index={index}
            count={options.length}
            sets={sets}
            onChange={(next) => updateOption(index, next)}
            onRemove={() => setOptions(options.filter((_, at) => at !== index))}
            onMove={(to) => moveOption(index, to)}
            lang={lang}
            base={base}
            importer={gridProps.importer}
            // the values this option's stored variants use: the sets that fit them
            usedValues={[...new Set(loadedVariants.concat(variants).map((variant) => variant?.options?.[option.key]).filter((key) => typeof key === "string" && key))].filter((key) => loadedVariants.some((variant) => variant?.options?.[option.key] === key))}
            // the only axis: its values are its variants, listed in its card
            renderRows={
              axes.length === 1 && axes[0] === option
                ? ({ toggle }) => (
                    <VariantsGrid
                      {...gridProps}
                      single={{
                        option,
                        // the last value offered stays: an axis of nothing sells nothing
                        toggle: (key) => {
                          const offered = offeredValues(option, sets)
                          if (offered.length === 1 && offered[0].key === key) {
                            showNotification({ message: "At least one value stays offered", className: "bg-admin_error text-white" })
                            return
                          }
                          toggle(key)
                        },
                      }}
                    />
                  )
                : null
            }
          />
        ))}
      </div>

      {/* two axes or more: a row per combination of their values, under the options */}
      {axes.length > 1 && (
        <div className="flex flex-col gap-3">
          <Typography component="h4" variant="admin_h5" className="my-0">
            Combinations
          </Typography>
          <VariantsGrid {...gridProps} />
        </div>
      )}
    </div>
  )
}
